import { RefObject, useEffect, useState } from 'react';

export type ScrollEdges = {
  atTop: boolean;
  atBottom: boolean;
};

const EDGE_THRESHOLD_PX = 1;

const INITIAL_EDGES: ScrollEdges = { atTop: true, atBottom: true };

const supportsScrollTimeline =
  typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('animation-timeline', 'scroll()');

/** Отслеживает, упёрся ли скролл элемента в верхний/нижний край — чтобы показывать тень прокрутки только там, где ещё есть что скроллить. */
const useScrollEdges = (ref: RefObject<HTMLElement | null>): ScrollEdges => {
  const [edges, setEdges] = useState<ScrollEdges>(INITIAL_EDGES);

  useEffect(() => {
    if (supportsScrollTimeline) return undefined;

    const element = ref.current;
    if (!element) return undefined;

    const updateEdges = () => {
      const { scrollTop, scrollHeight, clientHeight } = element;

      setEdges({
        atTop: scrollTop <= EDGE_THRESHOLD_PX,
        atBottom: scrollTop + clientHeight >= scrollHeight - EDGE_THRESHOLD_PX,
      });
    };

    updateEdges();

    element.addEventListener('scroll', updateEdges, { passive: true });

    const resizeObserver = new ResizeObserver(updateEdges);
    resizeObserver.observe(element);

    const mutationObserver = new MutationObserver(updateEdges);
    mutationObserver.observe(element, { childList: true, subtree: true, characterData: true });

    return () => {
      element.removeEventListener('scroll', updateEdges);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [ref]);

  return edges;
};

export default useScrollEdges;
